import type { ImportedModelUnitContext, ImportedTopologyBounds, ImportedTopologySource } from '../loaders/types'
import {
  computeImportedMeshDataBounds,
  resolveImportedTopologyBoundsScale,
} from './imported-model-topology-bounds'

/**
 * SolidWorks 毫米输入到当前渲染空间的换算因子。
 *
 * 设计说明：
 * 1. 该常量只用于 PropertyManager 等 SolidWorks 风格毫米输入到米制渲染空间的标量换算；
 * 2. 新版 sidecar BRep 几何不再使用该常量做 hydration 缩放，避免把后端已对齐 GLB 的几何再次缩小；
 * 3. 保留旧导出名是为了不破坏参考面等既有调用方的编译边界。
 */
export const IMPORTED_BREP_MM_TO_RENDER_SPACE_SCALE = 0.001

/**
 * sidecar/BRep 几何到当前渲染空间的默认缩放因子。
 *
 * 设计说明：
 * 1. 新版 sidecar 协议约定 BRep payload 与同一 GLB 渲染几何使用同一套最终导出单位；
 * 2. hydration 阶段不能再固定执行 `mm -> render space`，否则 part + single 会把 pick host 缩小 1000 倍；
 * 3. 保留独立常量是为了把“导入几何单位”和“SolidWorks 属性面板毫米输入换算”两类职责拆开。
 */
export const IMPORTED_BREP_TO_RENDER_SPACE_SCALE = 1

/**
 * 解析导入拓扑到 viewer 米制渲染空间的缩放比例。
 *
 * 设计说明：
 * 1. GLB 与 sidecar BRep 使用同一最终导出单位，单位上下文必须由 GLB parser JSON 读取；
 * 2. hydration 只消费 `ImportedTopologySource.unitContext`，禁止按零件/装配体类型写死缩放；
 * 3. 当旧数据缺少单位上下文时，按后端协议默认米制，返回 1。
 *
 * @param asset 导入拓扑源。
 * @returns sidecar BRep 到 viewer 米制渲染空间的缩放比例。
 */
export function resolveImportedPartTopologyRenderScale(asset: Pick<ImportedTopologySource, 'unitContext'>): number {
  const scaleFactorToMeter = asset.unitContext?.scaleFactorToMeter
  if (typeof scaleFactorToMeter === 'number' && Number.isFinite(scaleFactorToMeter) && scaleFactorToMeter > 0) {
    return scaleFactorToMeter
  }

  return IMPORTED_BREP_TO_RENDER_SPACE_SCALE
}

/**
 * 解析单个 body topology asset 的最终渲染缩放比例。
 *
 * 设计说明：
 * 1. 后端修复单位元数据后，`scaleFactorToMeter` 与明确 `unit` 是权威单位契约；
 * 2. bounds 对齐只能用于旧数据缺少单位契约时的兜底，不能覆盖显式 metadata；
 * 3. 该函数只在 hydration 时执行一次，不进入拾取、hover 或渲染循环。
 *
 * @param meshData worker 生成的 BRep 网格数据。
 * @param visibleBounds 当前 body binding 对应的 GLB 可见几何 bounds。
 * @param fallbackScale 单位 metadata 推导出的兜底比例。
 * @param fallbackScaleSource 当前比例来源；`default` 或缺失时才允许 bounds 兜底。
 * @returns BRep 到 visible mesh local space 的缩放比例。
 */
export function resolveImportedMeshDataRenderScale(
  meshData: Pick<MeshData, 'positions'>,
  visibleBounds: ImportedTopologyBounds | undefined,
  fallbackScale: number,
  fallbackScaleSource?: ImportedModelUnitContext['source'],
): number {
  const normalizedFallbackScale = Number.isFinite(fallbackScale) && fallbackScale > 0
    ? fallbackScale
    : IMPORTED_BREP_TO_RENDER_SPACE_SCALE

  if (fallbackScaleSource !== undefined && fallbackScaleSource !== 'default') {
    return normalizedFallbackScale
  }

  const boundsScale = resolveImportedTopologyBoundsScale(
    visibleBounds,
    computeImportedMeshDataBounds(meshData),
  )
  if (typeof boundsScale === 'number' && Number.isFinite(boundsScale) && boundsScale > 0) {
    return boundsScale
  }

  return normalizedFallbackScale
}

/**
 * 缩放单个拓扑点。
 *
 * @param point 原始拓扑点。
 * @param scale 缩放因子。
 * @returns 缩放后的拓扑点。
 */
function scaleTopologyPoint(point: TopologyPoint3D, scale: number): TopologyPoint3D {
  return [point[0] * scale, point[1] * scale, point[2] * scale]
}

/**
 * 缩放拓扑快照。
 *
 * 设计说明：
 * 1. `TopologySnapshot` 是选择、轮廓线和点线面代理的权威空间数据；
 * 2. 一旦 positions 被换算到渲染空间，快照中的 vertex / edge sample / face localPoint 也必须同步换算，
 * 否则会出现“模型正常、轮廓线或命中点线面偏大 1000 倍”的错位；
 * 3. 这里只做纯缩放，不做平移与旋转，保持“单位换算”和“实例变换”职责分离。
 *
 * @param snapshot 原始拓扑快照。
 * @param scale 缩放因子。
 * @returns 缩放后的拓扑快照。
 */
function scaleTopologySnapshot(snapshot: TopologySnapshot, scale: number): TopologySnapshot {
  return {
    vertices: snapshot.vertices.map((vertex) => ({
      ...vertex,
      position: scaleTopologyPoint(vertex.position, scale),
    })),
    edges: snapshot.edges.map((edge) => ({
      ...edge,
      sampledPoints: edge.sampledPoints?.map((point) => scaleTopologyPoint(point, scale)),
    })),
    faces: snapshot.faces.map((face) => ({
      ...face,
      localPoint: scaleTopologyPoint(face.localPoint, scale),
    })),
  }
}

/**
 * 把 sidecar/BRep 网格数据缩放到渲染空间。
 *
 * 设计说明：
 * 1. `MeshData` 按 `sha256` 在会话级缓存中复用，不能原地改写，否则会污染其他引用同一 BRep 的零件或装配实例；
 * 2. positions 与 topologySnapshot 都必须使用同一个单位换算结果，才能保证可见模型、默认轮廓线和点线面拾取完全重合；
 * 3. normals、indices 等与单位无关的数据直接复用，避免不必要的分配。
 *
 * @param meshData 原始 BRep 网格数据。
 * @param scale 缩放因子。
 * @returns 缩放到渲染空间后的网格数据。
 */
export function scaleImportedMeshDataToRenderSpace(meshData: MeshData, scale: number): MeshData {
  if (!Number.isFinite(scale) || scale <= 0 || Math.abs(scale - 1) <= 1e-12) {
    return meshData
  }

  const scaledPositions = new Float32Array(meshData.positions.length)
  for (let index = 0; index < meshData.positions.length; index += 1) {
    scaledPositions[index] = (meshData.positions[index] ?? 0) * scale
  }

  return {
    ...meshData,
    positions: scaledPositions,
    topologySnapshot: meshData.topologySnapshot
      ? scaleTopologySnapshot(meshData.topologySnapshot, scale)
      : meshData.topologySnapshot,
  }
}
